import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import apiService from '../services/api';
import '../styles/auth.css';

const ResetPassword = () => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, text: '', color: '' });

  const navigate = useNavigate();

  // Load email and OTP from localStorage on mount (hidden from user)
  useEffect(() => {
    const storedEmail = localStorage.getItem('reset_email');
    const storedOtp = localStorage.getItem('reset_otp');

    if (storedEmail) {
      setEmail(storedEmail);
    }
    if (storedOtp) {
      setOtp(storedOtp);
    }

    // If no email or OTP, redirect to forgot password
    if (!storedEmail || !storedOtp) {
      navigate('/forgot-password');
    }
  }, [navigate]);

  // Calculate password strength
  useEffect(() => {
    if (!password) {
      setPasswordStrength({ score: 0, text: '', color: '' });
      return;
    }

    let score = 0;
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password),
    };

    // Calculate score
    if (checks.length) score++;
    if (checks.uppercase) score++;
    if (checks.lowercase) score++;
    if (checks.number) score++;
    if (checks.special) score++;

    // Determine strength
    let text = '';
    let color = '';
    if (score <= 2) {
      text = 'Weak';
      color = '#ef4444';
    } else if (score === 3) {
      text = 'Fair';
      color = '#f59e0b';
    } else if (score === 4) {
      text = 'Good';
      color = '#3b82f6';
    } else {
      text = 'Strong';
      color = '#10b981';
    }

    setPasswordStrength({ score, text, color });
  }, [password]);

  // Handle password reset
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validate inputs
    if (!email || !otp || !password || !confirmPassword) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    // Validate OTP
    if (otp.length !== 6) {
      setError('OTP must be 6 digits');
      setLoading(false);
      return;
    }

    // Validate password length
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    // Validate password strength
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);

    if (!hasUppercase || !hasLowercase || !hasNumber) {
      setError('Password must contain uppercase, lowercase, and number');
      setLoading(false);
      return;
    }

    // Validate password match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      // Call API to reset password
      const response = await apiService.resetPassword(email, otp, password);

      if (response.success) {
        setSuccess('Password reset successfully!');
        
        // Clear localStorage
        localStorage.removeItem('reset_email');
        localStorage.removeItem('reset_otp');

        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login', { 
            state: { message: 'Password reset successful. Please login with your new password.' }
          });
        }, 2000);
      } else {
        setError(response.message || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="auth-shape shape-1"></div>
        <div className="auth-shape shape-2"></div>
        <div className="auth-shape shape-3"></div>
      </div>

      <div className="auth-content">
        <div className="auth-card" style={{ padding: '2rem', maxWidth: '480px' }}>
          <div className="auth-header" style={{ marginBottom: '1.25rem' }}>
            <h1 className="auth-title" style={{ fontSize: '1.75rem', margin: 0 }}>Reset Password</h1>
            <p className="auth-subtitle" style={{ fontSize: '0.875rem', marginTop: '0.25rem', color: '#94a3b8' }}>
              OTP verified! Please create a new password for your account
            </p>
          </div>

          {error && (
            <div className="auth-error" style={{ marginBottom: '1rem', padding: '0.75rem 1rem' }}>
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="auth-success" style={{ marginBottom: '1rem', padding: '0.75rem 1rem' }}>
              <CheckCircle size={18} />
              <span>{success}</span>
            </div>
          )}

          <form onSubmit={handleResetPassword} className="auth-form">
            {/* Password & Confirm Password (flex) */}
            <div className="form-row-flex" style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div className="form-group" style={{ flex: '1 1 50%', marginBottom: 0 }}>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    className="form-input"
                    placeholder="New Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="form-group" style={{ flex: '1 1 50%', marginBottom: 0 }}>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    className="form-input"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex="-1"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>

            {/* Password Feedback Info */}
            {(password || (confirmPassword && password === confirmPassword)) && (
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between', fontSize: '0.775rem', marginBottom: '0.75rem', padding: '0 0.25rem' }}>
                {password && (
                  <span style={{ color: passwordStrength.color }}>
                    Strength: {passwordStrength.text}
                  </span>
                )}
                {confirmPassword && password === confirmPassword && (
                  <span style={{ color: '#10b981' }}>
                    ✓ Passwords match
                  </span>
                )}
              </div>
            )}

            <p className="form-hint" style={{ fontSize: '0.775rem', color: '#94a3b8', margin: '0 0 1.25rem 0', lineHeight: '1.25' }}>
              Must be at least 8 characters with uppercase, lowercase, and number.
            </p>

            <button
              type="submit"
              className="auth-button"
              disabled={loading}
              style={{ padding: '0.75rem 1.5rem', fontSize: '0.95rem' }}
            >
              {loading ? (
                <>
                  <div className="button-spinner"></div>
                  Resetting Password...
                </>
              ) : (
                "Reset Password"
              )}
            </button>
          </form>

          <div className="auth-divider" style={{ margin: '1rem 0' }}>
            <span>or</span>
          </div>

          <div className="auth-footer" style={{ marginTop: '0.25rem' }}>
            <p style={{ fontSize: '0.85rem', margin: 0 }}>
              Remember your password?{' '}
              <Link to="/login" className="auth-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
